import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SubscriptionGuard - Wraps protected routes that require an active subscription
 * Redirects to /subscription if user doesn't have active subscription or trial
 */
const SubscriptionGuard = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    checkSubscription();
  }, [user]);

  const checkSubscription = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Auth via httpOnly cookie - no header needed
      const response = await axios.get(`${API}/subscription/status`);
      
      const status = response.data.status;
      // Allow access if active, trialing, or if trial hasn't expired
      const allowed = status === 'active' || status === 'trialing';
      setHasAccess(allowed);
    } catch {
      // On error, allow access (fail open for better UX)
      setHasAccess(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/subscription" replace />;
  }

  return children;
};

export default SubscriptionGuard;
